import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Inbox, Building2, Store, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import Pagination from '@/components/common/Pagination'
import DataTable from '@/components/common/DataTable'
import EmptyState from '@/components/common/EmptyState'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Clientes — la CARTERA del asesor cd-2b (docs/72).
 *
 * Hasta ahora "Mis clientes" apuntaba a la página de Referidos, que lista USUARIOS con su
 * estado de comisión: sin empresa, sin giro, sin tenant y sin edición. Aquí la unidad es el
 * NEGOCIO, que es lo que el asesor atiende.
 */

const ESTADOS = {
  active: { label: 'Activa', variant: 'success' },
  trialing: { label: 'En prueba', variant: 'warning' },
  past_due: { label: 'Con mora', variant: 'danger' },
  paused: { label: 'Pausada', variant: 'secondary' },
  canceled: { label: 'Cancelada', variant: 'secondary' },
}

/**
 * Chips de estado. El filtro lo aplica el SERVIDOR (`estado` en `getCartera`), por eso no llevan
 * conteo. El chip inicial sale de `?estado=` (Inicio enlaza a `/clientes?estado=trialing`).
 */
const FILTROS = [
  { key: '', label: 'Todas' },
  { key: 'trialing', label: 'En prueba' },
  { key: 'active', label: 'Activas' },
  { key: 'past_due', label: 'Con mora' },
  { key: 'paused', label: 'Pausadas' },
  { key: 'canceled', label: 'Canceladas' },
]

/**
 * ALCANCE vendido (docs/71 P8). El `!` marca el RECORTE DE CONTRATO: áreas que la edición
 * traería pero el paquete no incluye porque no se vendió su módulo. Se marca aparte porque
 * es lo único de esta pantalla que afecta al PRECIO — cambiarlo mueve el MRR.
 *
 * Solo el conteo, no un badge por módulo (`m.code` crudo, sin label — el backend no lo
 * resuelve por fila a propósito, ver comentario en `cd2bCarteraService.js`): en Empresarial
 * son 15-20 módulos, y listarlos todos aquí desbordaba la fila. El detalle con nombre real
 * e ícono ya vive en la Ficha (`ModulosVendidos`), a un clic — la fila entera navega ahí.
 *
 * Lo que el DUEÑO simplemente ocultó no aparece aquí: vive en el `edicionConfig` de su
 * tenant y resolverlo costaría una llamada por fila. Se ve al abrir la empresa.
 */
function Alcance({ empresa }) {
  const recorte = empresa.areasOffContrato || []
  const modulos = empresa.modulos || []
  if (modulos.length === 0 && recorte.length === 0) return <span className="text-subtle">—</span>
  return (
    <div className="flex flex-wrap items-center gap-1">
      {modulos.length > 0 && (
        <Badge variant="secondary">{modulos.length} módulo{modulos.length === 1 ? '' : 's'}</Badge>
      )}
      {recorte.length > 0 && (
        <Badge
          variant="danger"
          title={`Su paquete NO incluye: ${recorte.join(', ')}. Ampliarlo cambia el precio.`}
        >
          ! {recorte.length} sin vender
        </Badge>
      )}
    </div>
  )
}

const dinero = (n, moneda = 'USD') =>
  `${moneda} ${Number(n || 0).toLocaleString('es-SV', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

export default function Clientes() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const estado = FILTROS.some((f) => f.key && f.key === params.get('estado')) ? params.get('estado') : ''
  const [soloRecorte, setSoloRecorte] = useState(false)

  const fetchCartera = useCallback(async () => {
    setLoading(true)
    try {
      const filtros = {}
      if (estado) filtros.estado = estado
      if (busqueda) filtros.q = busqueda
      if (soloRecorte) filtros.conRecorte = '1'
      const res = await carteraService.getCartera(filtros, {
        page: page + 1,
        limit: rowsPerPage,
      })
      setItems(res?.data?.items || [])
      setTotal(res?.data?.pagination?.total || 0)
    } catch {
      toast.error('No se pudo cargar tu cartera')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, estado, busqueda, soloRecorte])

  useEffect(() => {
    fetchCartera()
  }, [fetchCartera])

  const elegirEstado = (k) => {
    const sig = new URLSearchParams(params)
    if (k) sig.set('estado', k); else sig.delete('estado')
    setParams(sig, { replace: true })
    setPage(0)
  }

  const filtrando = Boolean(estado || busqueda || soloRecorte)
  const vacio = (
    <EmptyState
      icono={<Inbox size={28} />}
      titulo={filtrando ? 'Ninguna empresa con ese filtro' : 'Aún no tienes empresas'}
      descripcion={filtrando ? 'Prueba con otro estado o borra la búsqueda.' : 'Aparecerán aquí las que vendas y las que te asignen.'}
    />
  )

  const badgeEstado = (e) => {
    const cfg = ESTADOS[e] || { label: e, variant: 'secondary' }
    return <Badge variant={cfg.variant}>{cfg.label}</Badge>
  }

  // Una sola definición para la tabla y las tarjetas del teléfono (antes eran dos copias a mano).
  const columnas = [
    {
      clave: 'empresa',
      titulo: 'Empresa',
      prioridad: 'titulo',
      celda: (e) => (
        <div className="min-w-0">
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-foreground">
            <Building2 size={15} className="shrink-0 text-subtle" />
            <span className="truncate">{e.empresaNombre || 'Sin nombre'}</span>
          </p>
          {/* En el teléfono el correo del dueño va aquí: la columna «Dueño» no sale en la tarjeta. */}
          <p className="truncate text-caption text-subtle md:hidden">{e.dueño?.email || '—'}</p>
          <p className="flex flex-wrap items-center gap-1.5 text-caption text-subtle">
            <span className="hidden md:inline">{e.tenantId || ''}</span>
            {e.esDemo && <Badge variant="warning">DEMO</Badge>}
            {e.negocios > 1 && (
              <span className="inline-flex items-center gap-1">
                <Store size={12} /> {e.negocios}
              </span>
            )}
          </p>
          {e.asesor && !e.asesor.comisionable && (
            <p className="mt-1 flex items-center gap-1 text-caption text-subtle md:hidden">
              <AlertTriangle size={13} className="shrink-0" /> La atiendes, pero la comisión es de otro asesor
            </p>
          )}
        </div>
      ),
    },
    {
      clave: 'dueno',
      titulo: 'Dueño',
      prioridad: 'oculta',
      celda: (e) => (
        <>
          <p className="text-foreground">{e.dueño?.nombre || '—'}</p>
          <p className="text-caption text-subtle">{e.dueño?.email}</p>
        </>
      ),
    },
    { clave: 'giro', titulo: 'Giro', prioridad: 'meta', celda: (e) => e.giro || '—' },
    {
      clave: 'edicion',
      titulo: 'Edición',
      prioridad: 'meta',
      celda: (e) => (
        <>
          <span className="text-foreground">{e.edicion || '—'}</span>
          {e.modalidad && <span className="block text-caption text-subtle">{e.modalidad}</span>}
        </>
      ),
    },
    { clave: 'alcance', titulo: 'Alcance', prioridad: 'meta', celda: (e) => <Alcance empresa={e} /> },
    { clave: 'estado', titulo: 'Estado', prioridad: 'valor', celda: (e) => badgeEstado(e.estado) },
    // El dinero a la derecha: una columna de cifras desalineada no se lee de un vistazo.
    {
      clave: 'mrr',
      titulo: 'MRR',
      alineacion: 'derecha',
      prioridad: 'valor',
      celda: (e) => <span className="font-semibold tabular-nums text-foreground">{e.mrr ? dinero(e.mrr, e.moneda) : '—'}</span>,
    },
    {
      clave: 'comision',
      titulo: 'Comisión',
      // Que el asesor vea cuáles le pagan y cuáles solo atiende. En el teléfono va como nota
      // bajo el nombre (solo cuando NO es suya), para no llenar la tarjeta de «Sí».
      prioridad: 'oculta',
      celda: (e) => (
        <Badge variant={e.asesor?.comisionable ? 'success' : 'secondary'}>
          {e.asesor?.comisionable ? 'Sí' : 'Solo atención'}
        </Badge>
      ),
    },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Mis clientes"
        description="Las empresas que atiendes: paquete, estado y facturación"
      />

      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
            />
            <Input
              placeholder="Buscar empresa o dueño y pulsa Enter"
              aria-label="Buscar empresa o dueño"
              enterKeyHint="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setBusqueda(search)
                  setPage(0)
                }
              }}
              className="pl-9"
            />
          </div>
          <Button
            variant={soloRecorte ? 'default' : 'outline'}
            aria-pressed={soloRecorte}
            onClick={() => { setSoloRecorte((v) => !v); setPage(0) }}
            title="Solo las empresas cuyo paquete recorta áreas de su edición"
          >
            <AlertTriangle size={15} /> Con recorte
          </Button>
        </div>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar sm:mx-0 sm:flex-wrap sm:px-0" role="tablist" aria-label="Filtrar por estado">
          {FILTROS.map((f) => {
            const on = estado === f.key
            return (
              <button
                key={f.key || 'todas'}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => elegirEstado(f.key)}
                className={cn(
                  'inline-flex h-9 shrink-0 items-center rounded-full border px-3.5 text-body-sm font-medium transition-colors',
                  on ? 'border-primary bg-primary text-white' : 'border-line bg-card text-foreground hover:border-neutral-300 dark:hover:border-neutral-600'
                )}
              >
                {f.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="space-y-3">
        <DataTable
          etiquetaTabla="Empresas de mi cartera"
          cargando={loading}
          filas={items}
          claveDeFila={(e) => e.subscriptionId}
          onFilaClick={(e) => navigate(`/clientes/${e.subscriptionId}`)}
          vacio={vacio}
          tarjetasEnMovil
          className="bg-card"
          columnas={columnas}
        />
        {!loading && items.length > 0 && (
          <Pagination
            className="rounded-lg border border-line bg-card"
            page={page}
            pageSize={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setRowsPerPage(s)
              setPage(0)
            }}
          />
        )}
      </div>
    </PageContainer>
  )
}
